import type { AudioScene } from './audio-scene.js';

export interface AudioSceneProfile {
  readonly tempo: number;
  readonly chords: readonly (readonly number[])[];
  readonly arp: readonly number[];
  readonly padWave: OscillatorType;
  readonly leadWave: OscillatorType;
  readonly cutoff: number;
  readonly pulse: number;
  readonly shimmer: number;
}

export type MusicTrackId =
  'NEON_RUSH' | 'BLOCK_STORM' | 'CRYSTAL_DRIVE' | 'ARENA_PULSE' | 'NIGHT_COMBO';

export interface MusicTrack {
  readonly id: MusicTrackId;
  readonly bars: number;
  readonly tempoMultiplier: number;
  readonly chordOffset: number;
  readonly bassPattern: readonly number[];
  readonly kickPattern: readonly number[];
  readonly hatPattern: readonly number[];
  readonly leadPattern: readonly number[];
  readonly leadOctave: number;
  readonly brightness: number;
}

/** Five deterministic arrangements rotate in this exact order. */
export const MUSIC_PLAYLIST: readonly MusicTrack[] = [
  {
    id: 'NEON_RUSH',
    bars: 8,
    tempoMultiplier: 1.16,
    chordOffset: 0,
    bassPattern: [0, -1, 0, 1, 0, -1, 2, 1],
    kickPattern: [1, 0, 0, 1, 1, 0, 1, 0],
    hatPattern: [0, 1, 0, 1, 0, 1, 1, 1],
    leadPattern: [0, 1, 2, 1, 0, 2, 1, 2],
    leadOctave: 2,
    brightness: 1.18,
  },
  {
    id: 'BLOCK_STORM',
    bars: 8,
    tempoMultiplier: 1.08,
    chordOffset: 2,
    bassPattern: [0, 0, -1, 2, 0, 1, -1, 0],
    kickPattern: [1, 0, 1, 0, 1, 1, 0, 1],
    hatPattern: [0, 1, 1, 1, 0, 1, 0, 1],
    leadPattern: [2, 1, 0, 1, 2, 0, 2, 1],
    leadOctave: 1,
    brightness: 0.9,
  },
  {
    id: 'CRYSTAL_DRIVE',
    bars: 8,
    tempoMultiplier: 1.12,
    chordOffset: 1,
    bassPattern: [0, -1, 1, -1, 0, 2, 1, -1],
    kickPattern: [1, 0, 0, 0, 1, 0, 0, 1],
    hatPattern: [1, 1, 0, 1, 1, 1, 0, 1],
    leadPattern: [2, 1, 2, 0, 2, 1, 0, 1],
    leadOctave: 2,
    brightness: 1.42,
  },
  {
    id: 'ARENA_PULSE',
    bars: 8,
    tempoMultiplier: 1.2,
    chordOffset: 3,
    bassPattern: [0, 1, 0, -1, 0, 2, 0, 1],
    kickPattern: [1, 0, 1, 1, 1, 0, 1, 0],
    hatPattern: [0, 1, 0, 1, 1, 1, 0, 1],
    leadPattern: [0, 2, 1, 2, 0, 1, 2, 1],
    leadOctave: 1,
    brightness: 1.08,
  },
  {
    id: 'NIGHT_COMBO',
    bars: 8,
    tempoMultiplier: 1.1,
    chordOffset: 1,
    bassPattern: [0, -1, 2, 0, -1, 1, 0, 2],
    kickPattern: [1, 0, 0, 1, 0, 1, 1, 0],
    hatPattern: [0, 1, 1, 0, 1, 1, 0, 1],
    leadPattern: [0, 0, 2, 1, 0, 2, 1, 0],
    leadOctave: 2,
    brightness: 0.76,
  },
] as const;

export function musicTrackAt(index: number): MusicTrack {
  const safeIndex =
    ((Math.trunc(index) % MUSIC_PLAYLIST.length) + MUSIC_PLAYLIST.length) % MUSIC_PLAYLIST.length;
  return MUSIC_PLAYLIST[safeIndex]!;
}

export function nextMusicTrackIndex(index: number): number {
  return (Math.trunc(index) + 1 + MUSIC_PLAYLIST.length) % MUSIC_PLAYLIST.length;
}

const profiles: Readonly<Record<AudioScene, AudioSceneProfile>> = {
  menu: {
    tempo: 76,
    chords: [
      [130.81, 164.81, 196],
      [110, 146.83, 174.61],
      [98, 130.81, 164.81],
      [123.47, 155.56, 196],
    ],
    arp: [0, 1, 2, 1],
    padWave: 'sine',
    leadWave: 'triangle',
    cutoff: 820,
    pulse: 0.08,
    shimmer: 0.15,
  },
  arena: {
    tempo: 96,
    chords: [
      [110, 164.81, 220],
      [98, 146.83, 196],
      [123.47, 185, 246.94],
      [130.81, 196, 261.63],
    ],
    arp: [0, 1, 2, 1, 2, 1],
    padWave: 'triangle',
    leadWave: 'sine',
    cutoff: 1_180,
    pulse: 0.5,
    shimmer: 0.26,
  },
  duel: {
    tempo: 104,
    chords: [
      [82.41, 123.47, 164.81],
      [92.5, 138.59, 185],
      [73.42, 110, 146.83],
      [98, 146.83, 196],
    ],
    arp: [0, 2, 1, 2, 0, 1],
    padWave: 'triangle',
    leadWave: 'triangle',
    cutoff: 980,
    pulse: 0.68,
    shimmer: 0.12,
  },
  result: {
    tempo: 72,
    chords: [
      [130.81, 196, 261.63],
      [164.81, 246.94, 329.63],
      [146.83, 220, 293.66],
    ],
    arp: [0, 1, 2, 1],
    padWave: 'sine',
    leadWave: 'sine',
    cutoff: 1_260,
    pulse: 0.06,
    shimmer: 0.48,
  },
  club: {
    tempo: 82,
    chords: [
      [110, 138.59, 185],
      [123.47, 155.56, 207.65],
      [98, 130.81, 174.61],
    ],
    arp: [0, 1, 2, 1],
    padWave: 'sine',
    leadWave: 'triangle',
    cutoff: 900,
    pulse: 0.14,
    shimmer: 0.2,
  },
  store: {
    tempo: 88,
    chords: [
      [146.83, 185, 246.94],
      [164.81, 207.65, 277.18],
      [130.81, 174.61, 220],
    ],
    arp: [0, 2, 1, 2],
    padWave: 'sine',
    leadWave: 'triangle',
    cutoff: 1_320,
    pulse: 0.12,
    shimmer: 0.56,
  },
  'path-neon': {
    tempo: 96,
    chords: [
      [110, 164.81, 246.94],
      [123.47, 185, 277.18],
      [98, 146.83, 220],
      [110, 164.81, 220],
    ],
    arp: [0, 1, 2, 1, 0, 2],
    padWave: 'triangle',
    leadWave: 'sine',
    cutoff: 1_420,
    pulse: 0.5,
    shimmer: 0.42,
  },
  'path-gold': {
    tempo: 88,
    chords: [
      [130.81, 196, 293.66],
      [164.81, 246.94, 329.63],
      [146.83, 220, 329.63],
      [123.47, 185, 277.18],
    ],
    arp: [0, 2, 1, 2, 1, 0],
    padWave: 'sine',
    leadWave: 'triangle',
    cutoff: 1_180,
    pulse: 0.38,
    shimmer: 0.68,
  },
  'path-ice': {
    tempo: 80,
    chords: [
      [146.83, 220, 440],
      [164.81, 246.94, 493.88],
      [123.47, 185, 369.99],
      [138.59, 207.65, 415.3],
    ],
    arp: [2, 1, 2, 0, 2, 1],
    padWave: 'sine',
    leadWave: 'sine',
    cutoff: 1_760,
    pulse: 0.22,
    shimmer: 0.92,
  },
  'path-dark': {
    tempo: 92,
    chords: [
      [73.42, 110, 164.81],
      [82.41, 123.47, 185],
      [65.41, 98, 146.83],
      [61.74, 92.5, 138.59],
    ],
    arp: [0, 0, 2, 1, 0, 1],
    padWave: 'triangle',
    leadWave: 'sine',
    cutoff: 680,
    pulse: 0.66,
    shimmer: 0.08,
  },
  'path-crystal': {
    tempo: 100,
    chords: [
      [196, 293.66, 587.33],
      [220, 329.63, 659.25],
      [174.61, 261.63, 523.25],
      [185, 277.18, 554.37],
    ],
    arp: [2, 0, 1, 2, 1, 0, 2, 1],
    padWave: 'sine',
    leadWave: 'triangle',
    cutoff: 2_080,
    pulse: 0.42,
    shimmer: 1,
  },
  'path-fire': {
    tempo: 108,
    chords: [
      [98, 146.83, 220],
      [110, 164.81, 246.94],
      [82.41, 123.47, 196],
      [92.5, 138.59, 207.65],
    ],
    arp: [0, 1, 0, 2, 1, 2, 0, 1],
    padWave: 'triangle',
    leadWave: 'triangle',
    cutoff: 1_080,
    pulse: 0.82,
    shimmer: 0.18,
  },
  'path-boss': {
    tempo: 112,
    chords: [
      [55, 82.41, 123.47],
      [61.74, 92.5, 138.59],
      [49, 73.42, 110],
      [55, 73.42, 110],
    ],
    arp: [0, 2, 0, 1, 0, 2, 1, 2],
    padWave: 'triangle',
    leadWave: 'triangle',
    cutoff: 760,
    pulse: 1,
    shimmer: 0.06,
  },
};

export function audioSceneProfile(scene: AudioScene): AudioSceneProfile {
  return profiles[scene];
}

export function musicBarDuration(scene: AudioScene, intensity: number, track: MusicTrack): number {
  const profile = audioSceneProfile(scene);
  const pressure = Math.min(1, Math.max(0, intensity));
  const bpm = profile.tempo * track.tempoMultiplier * (1 + pressure * 0.1);
  return (60 / bpm) * 4;
}

export function musicLayerCount(scene: AudioScene, intensity: number): number {
  const profile = audioSceneProfile(scene);
  const clamped = Math.min(1, Math.max(0, intensity));
  return 2 + Number(profile.pulse + clamped >= 0.62) + Number(profile.shimmer + clamped >= 0.78);
}
